"use client";

import React, {
  createElement,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  autocomplete,
  type AutocompleteApi,
  type AutocompleteOptions,
} from "@algolia/autocomplete-js";
import { createRoot } from "react-dom/client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import classnames from "classnames";
import { Xmark } from "iconoir-react";
import {
  getSourcesWithFilters,
  type QuickLink,
  type SearchItem,
} from "./GetSources";
import { SearchPanel } from "./SearchPanel";
import { RecentSearchRow, SourceHeader } from "./SourceTemplates";
import { DETACHED_MEDIA_QUERY } from "./constants";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";

import { getSearchParamsFromUrl } from "@/navigation/selectors";

import "@algolia/autocomplete-theme-classic";
import styles from "./SearchPanel.module.css";

/* Autocomplete is used in SearchBar.tsx, and you find its configuration here, like the plugins and the autocomplete instance.

Followed this guide to build this component: https://www.algolia.com/doc/ui-libraries/autocomplete/integrations/with-react-instantsearch/?client=Autocomplete.tsx */

function setSearchOpen(open: boolean) {
  document.documentElement.toggleAttribute(
    "data-search-open",
    open && !window.matchMedia(DETACHED_MEDIA_QUERY).matches,
  );
}

export type AutocompleteProps = Partial<AutocompleteOptions<SearchItem>> & {
  className?: string;
  initialQueryParams?: string | null;
  filter?: string | null;
  quickLinks?: QuickLink[];
};

export function Autocomplete({
  className,
  initialQueryParams,
  filter,
  quickLinks,
  ...autocompleteProps
}: AutocompleteProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const autocompleteInstance = useRef<AutocompleteApi<SearchItem> | null>(null);
  const panelRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
  const rootRef = useRef<
    Element | DocumentFragment | Document | ShadowRoot | null
  >(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const query = searchParams.get("query");
  const [searchTerm, refine] = useState(query ?? "");

  const fallbackQuery = useMemo(
    () => getSearchParamsFromUrl(initialQueryParams),
    [initialQueryParams],
  );

  const getSources = getSourcesWithFilters({
    filters: filter ?? undefined,
    quickLinks,
  });

  const plugins = useMemo(() => {
    // First plugin, shows recent searches
    const recentSearchPlugin = createLocalStorageRecentSearchesPlugin({
      key: "instantsearch",
      limit: 3,
      transformSource({ source, onRemove }) {
        return {
          ...source,
          onSelect({ item, setIsOpen }) {
            setIsOpen(false);
            router.push(`/search?query=${encodeURIComponent(item.label)}`); // Navigate to search page with the selected recent search, works both with mouse click and keyboard enter
          },
          getItems(params) {
            if (params.state.query) return [];
            return source.getItems(params);
          },
          templates: {
            header: () => <SourceHeader>Dine siste søk</SourceHeader>,
            item: ({ item }) => (
              <RecentSearchRow
                label={item.label}
                onRemove={() => onRemove(item.id)}
              />
            ),
          },
        };
      },
    });
    return [recentSearchPlugin];
  }, [router]);

  useEffect(() => {
    if (!autocompleteContainer.current) return;
    const instance = autocomplete<SearchItem>({
      detachedMediaQuery: DETACHED_MEDIA_QUERY,
      panelPlacement: "full-width",
      ...autocompleteProps,
      container: autocompleteContainer.current,
      initialState: { query: searchTerm },
      plugins,
      getSources: getSources,
      translations: {
        detachedCancelButtonText: "Avbryt",
        detachedSearchButtonTitle: "Søk",
        clearButtonTitle: "Tøm søkefeltet",
        submitButtonTitle: "Søk",
      },
      classNames: {
        form: "cvr-search! [--cvr-search-padding-x:0] [--cvr-search-font-size:0.875rem] pointer-coarse:[--cvr-search-font-size:1rem]",
        input:
          "[font-size:var(--cvr-search-font-size)]! placeholder-[var(--cvr-search-placeholder)]!",
        submitButton:
          "pl-4! [&_.aa-SubmitIcon]:text-[var(--cvr-search-placeholder)]!",
        detachedSearchButton:
          "cvr-search! gap-2.5 [--cvr-search-contrast:var(--cvr-search-placeholder)] [--cvr-search-padding-x:1rem] [&_.aa-DetachedSearchButtonIcon]:w-6! [&_.aa-DetachedSearchButtonIcon]:text-current! [&_svg]:size-6!",
        detachedContainer: styles.detached,
        panel: styles.panel,
      },
      onReset: () => refine(""),
      onSubmit: ({ state }) => {
        if (state.query.match(/^\d{13}$/))
          router.push(`/boker/isbn-${state.query}`);
        else if (state.query) router.push(`/search?query=${state.query}`);
        else if (fallbackQuery.size > 0)
          router.push(`/search?${fallbackQuery.toString()}`);
        else router.push("/search");
      },
      onStateChange: ({ prevState, state }) => {
        if (prevState.query !== state.query) refine(state.query);
        if (prevState.isOpen !== state.isOpen) setSearchOpen(state.isOpen);
      },
      navigator: {
        navigate: ({ itemUrl, item }) => {
          if ((item as Partial<QuickLink>).external)
            window.open(itemUrl, "_blank", "noopener,noreferrer");
          else if (/^https?:\/\//.test(itemUrl))
            window.location.assign(itemUrl);
          else router.push(itemUrl); // Navigate to book product page with keyboard enter
        },
      },
      renderer: {
        createElement,
        Fragment,
        render: () => {},
      },
      render: ({ elements, state }, root) => {
        const fieldBottom =
          autocompleteContainer.current?.getBoundingClientRect().bottom;
        if (fieldBottom !== undefined)
          root.style.setProperty(
            "--search-panel-offset",
            `${Math.round(fieldBottom)}px`,
          );
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;
          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }
        panelRootRef.current.render(
          <SearchPanel
            query={state.query}
            elements={elements as Record<string, ReactNode>}
          />,
        );
      },
    });
    autocompleteInstance.current = instance;
    return () => {
      instance.destroy();
      autocompleteInstance.current = null;
      setSearchOpen(false);
    };
    // Ignorning as autocompleteProps and query will cause flickering
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugins, router]);

  useEffect(() => {
    autocompleteInstance.current?.setIsOpen(false);
  }, [pathname, searchParams]);

  const closeSearch = () => {
    autocompleteInstance.current?.setIsOpen(false);
    autocompleteContainer.current?.querySelector("input")?.blur();
  };

  return (
    <div
      className={classnames(
        "relative w-full lg:search-open:contents",
        className,
      )}
    >
      <div
        className="relative w-full rounded-[var(--cvr-search-border-radius)] lg:search-open:col-start-2 lg:search-open:row-start-1"
        ref={autocompleteContainer}
      />
      <button
        type="button"
        onClick={closeSearch}
        className="hidden h-11 cursor-pointer items-center gap-2 cvr-label-lg lg:search-open:col-start-3 lg:search-open:row-start-1 lg:search-open:flex lg:search-open:justify-self-end"
      >
        Lukk
        <Xmark width={24} height={24} aria-hidden />
      </button>
    </div>
  );
}
